import { fetchMethods } from '../../../types/FetchService.types';
import { TCardUnitCreationParams } from '../hooks/useInitializeCard';

import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { restrictionsGroupBaseUrl, restrictionsGroupCardParam } from '@/services/utils';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import {
  PaymentCardActionName,
  IPaymentCard,
  TCardLimits,
  ISearchCard,
  IToggleCardOptions,
  IDigitizedCard,
  TDigitizedCardReasonCode,
  TDigitizedCardActions,
  digitizedCardActions,
  IEnrollment,
  ITransactionSimulation,
} from '@/features/cards/store/models/card.model';
import { TDashboardConfigSettingsCardsDesignType } from '@/store/models/dashboard-config.model';
import { TRestrictionsGroupType, IBaaSRestrictionsGroup } from '@/store/models/restriction.model';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { fetchQuery } from '@/utils/queryClient';
import { filtersToQueryParameters } from '@/utils/filtersToQueryParameters';
import { TCardActionsPayloadType } from '@/features/cards/store/types/card.types';

export class CardService {
  static queryKey = 'Cards';

  static getCard = async (cardId: string | number): Promise<IPaymentCard> => {
    try {
      const config = getStaticDashboardConfig();
      const url = `${config.api.baseUrl}/v1/cards/${cardId.toString()}`;
      const cardsResponse = await fetchQuery([this.queryKey, { action: 'get', method: fetchMethods.GET }], url, {
        headers: createHttpHeader(),
      });

      const results = await decryptTreezorResponse<IPaymentCard>(cardsResponse);
      return results[0];
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static searchCards = async (filters?: ISearchCard): Promise<IPaymentCard[]> => {
    try {
      const config = getStaticDashboardConfig();
      const params = filtersToQueryParameters(filters);

      const url = `${config.api.baseUrl}/v1/cards?${params.toString()}`;
      const cardsResponse = await fetchQuery(
        [this.queryKey, { action: 'search', method: fetchMethods.GET, params: Object.fromEntries(params) }],
        url,
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse<IPaymentCard>(cardsResponse, { isSequential: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static toggleOptions = async (cardId: number, options: IToggleCardOptions): Promise<IPaymentCard> => {
    try {
      const config = getStaticDashboardConfig();

      const cardsResponse = await fetchQuery(
        [this.queryKey, { action: 'toggle', method: fetchMethods.PUT, id: cardId, params: options }],
        `${config.api.baseUrl}/v1/cards/${cardId}/Options/`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(options),
        },
      );

      const [card] = await decryptTreezorResponse<IPaymentCard>(cardsResponse);
      return card;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static setCardActions = async (
    cardId: number,
    action: PaymentCardActionName,
    payload: TCardActionsPayloadType,
  ): Promise<IPaymentCard> => {
    try {
      const config = getStaticDashboardConfig();
      const url = `${config.api.baseUrl}/v1/cards/${cardId}/${action}`;

      const method = fetchMethods.PUT;

      const cardsResponse = await fetchQuery(
        [this.queryKey, { method, id: cardId, params: { action, payload } }],
        url,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(payload),
        },
      );

      const [card] = await decryptTreezorResponse<IPaymentCard>(cardsResponse);
      return card;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static unblockCvc = async (cardId: number, action: PaymentCardActionName): Promise<void> => {
    try {
      const config = getStaticDashboardConfig();

      const url = `${config.api.baseUrl}/v1/cards/${cardId}/${action}`;

      const response = await fetchQuery([this.queryKey, { method: fetchMethods.PUT, id: cardId, action }], url, {
        headers: createHttpHeader(),
      });

      if (!response.ok) throw new Error(`Unblock CVC failed with status ${response.status}`);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateCard = async (cardId: number, updatedCard: Partial<IPaymentCard>): Promise<IPaymentCard> => {
    try {
      const config = getStaticDashboardConfig();

      const cardsResponse = await fetchQuery(
        [this.queryKey, { action: 'update', method: fetchMethods.PUT, id: cardId, params: updatedCard }],
        `${config.api.baseUrl}/v1/cards/${cardId}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(updatedCard),
        },
      );

      const [card] = await decryptTreezorResponse<IPaymentCard>(cardsResponse);
      return card;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static createCard = async (
    newCard: TCardUnitCreationParams,
    type: TDashboardConfigSettingsCardsDesignType,
  ): Promise<IPaymentCard> => {
    try {
      const config = getStaticDashboardConfig();

      const cardsResponse = await fetchQuery(
        [this.queryKey, { action: 'createCard', method: fetchMethods.POST, id: newCard.userId, params: newCard }],
        `${config.api.baseUrl}/v1/cards/${type === 'physic' ? 'RequestPhysical' : 'CreateVirtual'}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ ...newCard, cardTag: 'TrzDashboard creation' }),
        },
      );

      const [card] = await decryptTreezorResponse<IPaymentCard>(cardsResponse);

      return card;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static setCardLimits = async (cardId: number, cardLimits: TCardLimits<string | number>): Promise<IPaymentCard> => {
    try {
      const config = getStaticDashboardConfig();
      const {
        limitAtmAll,
        limitAtmDay,
        limitAtmMonth,
        limitAtmWeek,
        limitAtmYear,
        limitPaymentAll,
        limitPaymentDay,
        limitPaymentMonth,
        limitPaymentWeek,
        limitPaymentYear,
      } = Object.keys(cardLimits).reduce<TCardLimits<string | number>>((prev, current) => {
        const value = cardLimits[current];
        return {
          ...prev,
          [current]: value === undefined || value === '' ? 0 : value,
        };
      }, cardLimits);

      const cardsResponse = await fetchQuery(
        [this.queryKey, { action: 'Set Limit', method: fetchMethods.PUT, id: cardId, params: cardLimits }],
        `${config.api.baseUrl}/v1/cards/${cardId}/Limits/`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({
            limitAtmAll,
            limitAtmDay,
            limitAtmMonth,
            limitAtmWeek,
            limitAtmYear,
            limitPaymentAll,
            limitPaymentDay,
            limitPaymentMonth,
            limitPaymentWeek,
            limitPaymentYear,
          }),
        },
      );
      const [card] = await decryptTreezorResponse<IPaymentCard>(cardsResponse);
      return card;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static searchCustomRestrictionsGroup = async (
    restrictionsGroupId: number,
    type: TRestrictionsGroupType,
  ): Promise<IBaaSRestrictionsGroup> => {
    try {
      const config = getStaticDashboardConfig();
      const restrictionsGroupsResponse = await fetchQuery(
        [
          this.queryKey,
          { action: 'Search Restrictions Group', method: fetchMethods.GET, id: restrictionsGroupId, params: { type } },
        ],
        `${config.api.baseUrl}/v1/${restrictionsGroupBaseUrl[type]}/${restrictionsGroupId}`,
        {
          headers: createHttpHeader(),
        },
      );

      const [restrictionsGroup] = await decryptTreezorResponse<IBaaSRestrictionsGroup>(restrictionsGroupsResponse, {
        shouldSkipEncryption: true,
      });
      return restrictionsGroup;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static addCustomRestrictionsGroup = async (
    newRestrictionsGroup: IBaaSRestrictionsGroup,
    type: TRestrictionsGroupType,
  ): Promise<IBaaSRestrictionsGroup> => {
    try {
      const config = getStaticDashboardConfig();
      const { id, createdDate, ...restrictionsGroupRequest } = newRestrictionsGroup;

      const restrictionsGroupsResponse = await fetchQuery(
        [
          this.queryKey,
          {
            action: 'Add Registrations Group',
            method: fetchMethods.POST,
            id: newRestrictionsGroup.id,
            params: { type, restrictionsGroupRequest },
          },
        ],
        `${config.api.baseUrl}/v1/${restrictionsGroupBaseUrl[type]}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ ...restrictionsGroupRequest }),
        },
      );
      const [restrictionsGroup] = await decryptTreezorResponse<IBaaSRestrictionsGroup>(restrictionsGroupsResponse, {
        shouldSkipEncryption: true,
      });
      return restrictionsGroup;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static updateCustomRestrictionsGroup = async (
    restriction: IBaaSRestrictionsGroup,
    type: TRestrictionsGroupType,
  ): Promise<IBaaSRestrictionsGroup> => {
    try {
      const config = getStaticDashboardConfig();
      delete restriction.createdDate;
      delete restriction.modifiedDate;
      const { id, ...restrictionCard } = restriction;

      const restrictionsRes = await fetchQuery(
        [
          this.queryKey,
          {
            action: 'Update Restrictions Group',
            method: fetchMethods.PUT,
            id: restriction.id,
            params: { type, restrictionCard },
          },
        ],
        `${config.api.baseUrl}/v1/${restrictionsGroupBaseUrl[type]}/${id}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ ...restrictionCard }),
        },
      );
      const cryptedRestrictionsRes = await decryptTreezorResponse<IBaaSRestrictionsGroup>(restrictionsRes, {
        shouldSkipEncryption: true,
      });
      return cryptedRestrictionsRes[0];
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static deleteCardRestrictionsGroup = async (
    cardId: number,
    deletedData: Partial<IPaymentCard>,
  ): Promise<IPaymentCard> => {
    try {
      const config = getStaticDashboardConfig();
      const url = `${config.api.baseUrl}/v1/cards/${cardId}`;

      const init = {
        headers: createHttpHeader(),
        body: JSON.stringify(deletedData),
      };

      const cardsResponse = await fetchQuery(
        [
          this.queryKey,
          { action: 'Delete Restricitons Group', method: fetchMethods.PUT, id: cardId, params: deletedData },
        ],
        url,
        init,
      );
      const [card] = await decryptTreezorResponse<IPaymentCard>(cardsResponse);
      return card;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static checkRestrictionLinkedToCard = async (
    restrictionsGroupId: number,
    type: TRestrictionsGroupType,
  ): Promise<void> => {
    try {
      const config = getStaticDashboardConfig();
      const params = new URLSearchParams();
      params.set(restrictionsGroupCardParam[type], restrictionsGroupId.toString());

      const cardsResponse = await fetchQuery(
        [
          this.queryKey,
          {
            action: 'Check Restrictions Group Linked',
            method: fetchMethods.GET,
            id: restrictionsGroupId,
            params: { type },
          },
        ],
        `${config.api.baseUrl}/v1/cards?${params.toString()}`,
        {
          headers: createHttpHeader(),
        },
      );
      const uncryptedCardResponse = await decryptTreezorResponse(cardsResponse, { shouldSkipEncryption: true });

      if (uncryptedCardResponse?.length > 0)
        return Promise.reject(`At least one card is attached to this restriction, you can't delete this restriction`);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static blockCard = async (cardId: number, lockStatus: number): Promise<IPaymentCard> => {
    try {
      const config = getStaticDashboardConfig();

      const cardsResponse = await fetchQuery(
        [this.queryKey, { action: 'Block', method: fetchMethods.PUT, id: cardId, params: { lockStatus } }],
        `${config.api.baseUrl}/v1/cards/${cardId}/LockUnlock`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ lockStatus }),
        },
      );

      const [card] = await decryptTreezorResponse<IPaymentCard>(cardsResponse);
      return card;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getDigitizedCards = async (cardId: number): Promise<IDigitizedCard[]> => {
    try {
      const config = getStaticDashboardConfig();

      const url = `${config.api.baseUrl}/v1/cards/${cardId}/digitalizedCards`;
      const cardsResponse = await fetchQuery([this.queryKey, { method: fetchMethods.GET, id: cardId }], url, {
        headers: createHttpHeader(),
      });

      return await decryptTreezorResponse<IDigitizedCard>(cardsResponse);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static setDigitizedCarAction = async (
    digitizedCardId: string,
    action: TDigitizedCardActions,
    reasonCode: TDigitizedCardReasonCode,
  ) => {
    try {
      const config = getStaticDashboardConfig();

      let url = `${config.api.baseUrl}/v1/cards/digitalizedCards/${digitizedCardId}`;
      const options = {
        headers: createHttpHeader(),
        body: '',
      };

      if (action === digitizedCardActions.DELETE) {
        url += `?reasonCode=${reasonCode}`;
      } else {
        options.body = JSON.stringify({
          status: action,
          reasonCode,
        });
      }

      const apiResponse = await fetchQuery(
        [
          this.queryKey,
          {
            method: action === digitizedCardActions.DELETE ? fetchMethods.DELETE : fetchMethods.PUT,
            id: digitizedCardId,
          },
        ],
        url,
        options,
      );
      const result = await apiResponse.json();
      return result;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getCardEnrollment = async (cardId: number): Promise<IEnrollment[]> => {
    try {
      const config = getStaticDashboardConfig();

      const url = `${config.api.baseUrl}/v1/cards/${cardId}/authentication-methods`;
      const response = await fetchQuery(['card-enrollment', { method: fetchMethods.GET, id: cardId }], url, {
        headers: createHttpHeader(),
      });

      return await decryptTreezorResponse<IEnrollment>(response);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static enrollCard = async (
    cardId: number,
    action: PaymentCardActionName.REGISTER_3DS_OTPSMS | PaymentCardActionName.REGISTER_3DS_OOB,
  ): Promise<IEnrollment> => {
    try {
      const config = getStaticDashboardConfig();
      let url = `${config.api.baseUrl}/v1/cards/${cardId}/${action}`;
      if (action === PaymentCardActionName.REGISTER_3DS_OTPSMS) url = `${config.api.baseUrl}/v1/cards/${action}`;

      const payload = action === PaymentCardActionName.REGISTER_3DS_OOB ? { method: 'OOB' } : { cardId };

      const cardsResponse = await fetchQuery(
        ['card-enrollment', { method: fetchMethods.POST, id: cardId, params: { action, payload } }],
        url,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(payload),
        },
      );

      return await decryptTreezorResponse<IEnrollment>(cardsResponse, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static deleteOOBEnrollment = async (cardId: number, authMethodId: string): Promise<string> => {
    try {
      const config = getStaticDashboardConfig();

      const url = `${config.api.baseUrl}/v1/cards/${cardId}/authentication-methods/${authMethodId}`;
      await fetchQuery(['card-enrollment', { method: fetchMethods.DELETE, id: authMethodId }], url, {
        headers: createHttpHeader(),
      });

      return authMethodId;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static simulateCardTransactions = async (
    form: ITransactionSimulation & { publicToken: string },
  ): Promise<ITransactionSimulation> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        ['simulateCardTransactions', { method: fetchMethods.POST }],
        `${config.api.baseUrl}/simulation/cardtransactions`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(form),
        },
      );
      return await decryptTreezorResponse<ITransactionSimulation>(response, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
